import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { 
  Users, Shield, UserCog, Search, RefreshCw, 
  Crown, UserCheck, UserX, Clock, AlertTriangle,
  Mail, Building2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role definitions
const ROLES = {
  super_admin: { label: 'Super Admin', color: 'bg-amber-500/20 text-amber-400', icon: Crown },
  admin: { label: 'Admin', color: 'bg-blue-500/20 text-blue-400', icon: Shield },
  client_admin: { label: 'Client Admin', color: 'bg-purple-500/20 text-purple-400', icon: UserCog },
  user: { label: 'User', color: 'bg-zinc-500/20 text-zinc-400', icon: Users }
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);

  // Note: Access control is handled by ProtectedRoute - no need to check here

  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-logs?action_type=role`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || data || []);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success(`Role updated to ${ROLES[newRole]?.label || newRole}`);
        fetchUsers();
        fetchAuditLogs();
        setShowRoleModal(false);
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchUsers();
      } else {
        toast.error('Failed to update user status');
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleGrantSuperAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to grant Super Admin privileges? This action will be logged.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/grant-super-admin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_user_id: userId })
      });

      if (response.ok) {
        toast.success('Super Admin privileges granted');
        fetchUsers();
        fetchAuditLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to grant Super Admin');
      }
    } catch (error) {
      toast.error('Failed to grant Super Admin');
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getUserRole = (user) => {
    if (user.system_role === 'super_admin' || user.roles?.includes('super_admin')) {
      return 'super_admin';
    }
    if (user.roles?.includes('admin')) return 'admin';
    if (user.roles?.includes('client_admin')) return 'client_admin';
    return 'user';
  };

  const getRoleBadge = (role) => {
    const config = ROLES[role] || ROLES.user;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Crown className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
              <p className="text-sm text-muted-foreground">
                Super Admin Control Panel
              </p>
            </div>
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Super Admin Only
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800/50 border border-zinc-700">
            <TabsTrigger value="users" className="data-[state=active]:bg-primary">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-primary">
              <Clock className="w-4 h-4 mr-2" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Users</CardTitle>
                    <CardDescription>Manage user accounts and roles</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64 bg-zinc-800/50 border-zinc-700"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-primary" />
                    <p className="text-zinc-500">Loading users...</p>
                  </div>
                ) : filteredUsers.length > 0 ? (
                  <div className="space-y-3">
                    {filteredUsers.map((user) => {
                      const role = getUserRole(user);
                      const isCurrentUser = user.user_id === currentUser?.user_id;
                      
                      return (
                        <div
                          key={user.user_id}
                          className={`p-4 rounded-lg border transition-colors ${
                            isCurrentUser 
                              ? 'bg-primary/5 border-primary/20' 
                              : 'bg-zinc-800/30 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                                {user.picture ? (
                                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                                ) : (
                                  <span className="text-sm font-medium">
                                    {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                                  </span>
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{user.name || 'Unnamed User'}</h4>
                                  {isCurrentUser && (
                                    <Badge variant="outline" className="text-[10px]">You</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-sm text-zinc-400 flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {user.email}
                                  </span>
                                  {user.active_workspace_name && (
                                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                                      <Building2 className="w-3 h-3" />
                                      {user.active_workspace_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {getRoleBadge(role)}
                              
                              <Badge className={user.status === 'active' 
                                ? 'bg-emerald-500/20 text-emerald-400' 
                                : 'bg-red-500/20 text-red-400'
                              }>
                                {user.status === 'active' ? (
                                  <><UserCheck className="w-3 h-3 mr-1" /> Active</>
                                ) : (
                                  <><UserX className="w-3 h-3 mr-1" /> Inactive</>
                                )}
                              </Badge>

                              {!isCurrentUser && (
                                <div className="flex items-center gap-1">
                                  {role !== 'super_admin' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleGrantSuperAdmin(user.user_id)}
                                      title="Grant Super Admin"
                                    >
                                      <Crown className="w-4 h-4 text-amber-400" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleUserStatus(user.user_id, user.status)}
                                    title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                                  >
                                    {user.status === 'active' ? (
                                      <UserX className="w-4 h-4 text-red-400" />
                                    ) : (
                                      <UserCheck className="w-4 h-4 text-emerald-400" />
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* User details row */}
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-700/50 text-xs text-zinc-500">
                            <span>ID: {user.user_id?.substring(0, 12)}...</span>
                            <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
                            {user.last_login && (
                              <span>Last login: {new Date(user.last_login).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No users found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle>Role Change Audit Log</CardTitle>
                <CardDescription>Track all role modifications</CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogs.length > 0 ? (
                  <div className="space-y-3">
                    {auditLogs.map((log, idx) => (
                      <div
                        key={log.log_id || idx}
                        className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-amber-400" />
                              <span className="font-medium text-sm">{log.action}</span>
                            </div>
                            <p className="text-xs text-zinc-400">
                              {log.details?.description || JSON.stringify(log.details)}
                            </p>
                          </div>
                          <div className="text-right text-xs text-zinc-500">
                            <p>{new Date(log.timestamp).toLocaleString()}</p>
                            <p className="mt-1">By: {log.performed_by}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No audit logs found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
